import React, { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Upload, ArrowRight, CheckCircle, Warning, FileArrowUp, Download } from "@phosphor-icons/react";
import { toast } from "sonner";

const STEPS = ["Upload", "Preview", "Map Columns", "Validate", "Import"];

export default function DataImport() {
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [importBills, setImportBills] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileInput = useRef(null);

  const uploadForPreview = async (f) => {
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", f);
      const { data } = await api.post("/import/preview", form, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      setMapping(data.suggested_mapping || {});
      setFile(f);
      setStep(2);
      toast.success(`Loaded ${data.row_count} rows`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const runImport = async () => {
    // Validate required
    const missing = ["date", "name", "mobile", "service"].filter((k) => !mapping[k]);
    if (missing.length) { toast.error(`Map required fields: ${missing.join(", ")}`); return; }
    setBusy(true); setStep(4);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("mapping", JSON.stringify(mapping));
      form.append("import_bills", importBills);
      const { data } = await api.post("/import/execute", form, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`Imported ${data.applications_created} applications (${data.customers_created} new customers).`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1200px] space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-indigo-600 font-semibold mb-2 flex items-center gap-2">
          <Upload size={14} weight="fill" /> Data Migration
        </div>
        <h1 className="text-3xl lg:text-4xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>Import Old Data</h1>
        <p className="text-sm text-slate-600 mt-1.5">Migrate years of applications from Excel / CSV. Duplicates auto-detected by mobile.</p>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2 flex-wrap">
        {STEPS.map((label, idx) => (
          <React.Fragment key={label}>
            <div className={`flex items-center gap-2 text-xs ${idx <= step ? "text-indigo-700 font-semibold" : "text-slate-400"}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${idx < step ? "bg-emerald-600 text-white" : idx === step ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-500 border border-slate-200"}`}>
                {idx < step ? "✓" : idx + 1}
              </div>
              <span>{label}</span>
            </div>
            {idx < STEPS.length - 1 && <ArrowRight size={12} className="text-slate-300" />}
          </React.Fragment>
        ))}
      </div>

      {/* Step 0-1: Upload */}
      {step < 2 && (
        <Card className="p-8 bg-white border-dashed border-2 border-slate-300 text-center">
          <FileArrowUp size={48} weight="thin" className="mx-auto text-slate-400 mb-3" />
          <div className="text-slate-600 mb-4 text-sm">Drop your <b>.xlsx / .xls / .csv</b> file here, or click to choose.</div>
          <input
            ref={fileInput}
            type="file"
            accept=".xlsx,.csv"
            className="hidden"
            data-testid="import-file-input"
            onChange={(e) => e.target.files?.[0] && uploadForPreview(e.target.files[0])}
          />
          <Button onClick={() => fileInput.current?.click()} disabled={busy} className="gap-2 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="import-choose-btn">
            <Upload size={16} weight="bold" /> {busy ? "Reading…" : "Choose File"}
          </Button>
          <div className="mt-6 text-[11px] text-slate-500 space-y-1">
            <div><b>Required columns:</b> Date, Customer Name, Mobile, Service</div>
            <div><b>Optional:</b> App No, Tracking No, Token No, Status, Portal/Department, Operator, Bill Amount, Paid Amount, Balance, Remarks, Email, Aadhaar, PAN, Address</div>
          </div>
          <a href="/sample-import.csv" download className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline mt-3">
            <Download size={11} /> Download sample template
          </a>
        </Card>
      )}

      {/* Step 2: Column Mapping */}
      {step === 2 && preview && (
        <div className="space-y-4">
          <Card className="p-4 bg-white">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="font-semibold">{preview.filename}</div>
                <div className="text-xs text-slate-500">{preview.row_count} rows · {preview.headers.length} columns</div>
              </div>
              <Button variant="outline" onClick={() => { setStep(0); setPreview(null); setFile(null); }}>Change File</Button>
            </div>

            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2">Match ERP fields to your Excel columns</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {preview.targets.map((t) => (
                <div key={t.key} className="flex items-center gap-2">
                  <div className={`w-40 text-sm ${t.required ? "font-semibold text-slate-900" : "text-slate-600"}`}>
                    {t.label}
                    {t.required && <span className="text-rose-600 ml-1">*</span>}
                  </div>
                  <Select
                    value={mapping[t.key] || "__none__"}
                    onValueChange={(v) => {
                      const next = { ...mapping };
                      if (v === "__none__") delete next[t.key]; else next[t.key] = v;
                      setMapping(next);
                    }}
                  >
                    <SelectTrigger data-testid={`import-map-${t.key}`} className="h-8 flex-1 text-xs"><SelectValue placeholder="— skip —" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— skip —</SelectItem>
                      {preview.headers.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-2 mt-4 pt-3 border-t border-slate-100">
              <Checkbox id="import-bills" checked={importBills} onCheckedChange={setImportBills} data-testid="import-bills-cb" />
              <label htmlFor="import-bills" className="text-sm text-slate-700 cursor-pointer">Also treat bill amounts as historical payments (marks as Paid / Partial)</label>
            </div>
          </Card>

          {/* Preview rows */}
          <Card className="p-4 bg-white">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2">Preview (first 5 rows)</div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-slate-500 border-b border-slate-200">
                  <tr>{preview.headers.map((h) => <th key={h} className="text-left py-1.5 pr-3 font-medium">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {preview.preview_rows.map((r, idx) => (
                    <tr key={idx} className="border-b border-slate-100">
                      {preview.headers.map((h) => <td key={h} className="py-1.5 pr-3 text-slate-700">{r[h] || <span className="text-slate-300">—</span>}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
            <Button onClick={runImport} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700 text-white gap-2" data-testid="import-run-btn">
              <Upload size={16} weight="bold" /> Import {preview.row_count} rows
            </Button>
          </div>
        </div>
      )}

      {/* Step 3-4: Result */}
      {step >= 3 && (
        <Card className="p-6 bg-white">
          {busy && <div className="text-center text-slate-500 py-10">Importing… please wait, do not close this tab.</div>}
          {result && (
            <div className="space-y-4">
              <div className="text-center">
                <CheckCircle size={48} weight="fill" className="mx-auto text-emerald-500 mb-2" />
                <div className="text-2xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>Import Complete</div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <ResultCard label="Processed" value={result.processed} accent="indigo" />
                <ResultCard label="New Customers" value={result.customers_created} accent="emerald" />
                <ResultCard label="Matched Customers" value={result.customers_matched} accent="amber" />
                <ResultCard label="Applications" value={result.applications_created} accent="violet" />
              </div>
              {result.error_count > 0 && (
                <Card className="p-3 bg-amber-50 border-amber-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Warning size={16} weight="fill" className="text-amber-600" />
                    <div className="font-semibold text-amber-800">{result.error_count} row(s) had errors</div>
                  </div>
                  <div className="max-h-48 overflow-y-auto text-xs space-y-1">
                    {result.errors.map((e, i) => (
                      <div key={i} className="font-mono text-slate-700">Row {e.row}: {e.reason}</div>
                    ))}
                  </div>
                </Card>
              )}
              <div className="flex justify-center gap-2">
                <Button variant="outline" onClick={() => { setStep(0); setResult(null); setPreview(null); setFile(null); setMapping({}); }}>Import Another File</Button>
                <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => window.location.href = "/applications"}>View Applications</Button>
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

function ResultCard({ label, value, accent }) {
  const map = { indigo: "border-l-indigo-500", emerald: "border-l-emerald-500", amber: "border-l-amber-500", violet: "border-l-violet-500" };
  return (
    <Card className={`p-3 border-l-4 ${map[accent] || map.indigo} bg-white`}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">{label}</div>
      <div className="text-2xl font-bold text-slate-900 mt-0.5" style={{ fontFamily: "var(--font-heading)" }}>{value}</div>
    </Card>
  );
}
